import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BoardResponse } from "@breeze/shared";

async function postReadState(
  cardId: number,
  action: "read" | "unread"
): Promise<{ lastReadAt: string | null }> {
  const res = await fetch(`/api/cards/${cardId}/${action}`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`${action} failed (${res.status})`);
  const body = (await res.json()) as { lastReadAt?: string | null };
  return { lastReadAt: body.lastReadAt ?? null };
}

// Updates every cached board variant's lastReadAt for the given card,
// so the SSE echo + self-origin update stay consistent. Read-state is
// team-shared so every client ends up with the same value.
function patchCache(
  qc: ReturnType<typeof useQueryClient>,
  cardId: number,
  lastReadAt: string | null,
  flaggedAt: string | null
): void {
  qc.getQueryCache()
    .findAll({ queryKey: ["board"] })
    .forEach((query) => {
      const prev = query.state.data as BoardResponse | undefined;
      if (!prev) return;
      const cards = prev.cards.map((c) =>
        c.cardId === cardId ? { ...c, lastReadAt, flaggedAt } : c
      );
      qc.setQueryData(query.queryKey, { ...prev, cards });
    });
}

export function useMarkRead(): ReturnType<
  typeof useMutation<{ lastReadAt: string | null }, Error, number>
> {
  const qc = useQueryClient();
  return useMutation<{ lastReadAt: string | null }, Error, number>({
    mutationFn: (cardId) => postReadState(cardId, "read"),
    // Reading clears both the time-based unread and the manual flag.
    onSuccess: ({ lastReadAt }, cardId) =>
      patchCache(qc, cardId, lastReadAt, null),
  });
}

export function useMarkUnread(): ReturnType<
  typeof useMutation<{ lastReadAt: string | null }, Error, number>
> {
  const qc = useQueryClient();
  return useMutation<{ lastReadAt: string | null }, Error, number>({
    mutationFn: (cardId) => postReadState(cardId, "unread"),
    // Unread clears the read timestamp and sets the manual flag locally.
    onSuccess: (_res, cardId) =>
      patchCache(qc, cardId, null, new Date().toISOString()),
  });
}
